import React, { useState, useEffect } from 'react';
import {
  View,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  TouchableWithoutFeedback,
  Keyboard,
  ScrollView,
  Platform,
  useWindowDimensions,
} from 'react-native';

import { axiosinstance } from '../../axiosClient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import { useAppSelector } from '../../redux/helper';
import { isTablet } from 'react-native-device-info';
import { shouldUseWebLayout } from '../../utils/layoutUtils';
import { fs, s, v, borderWidth } from '../../utils/libs';
import { GTFonts, theme, useLayoutSize, useTheme } from '../../theme';
import Typography from '../../components/Reusables/Typography';
import appAnalytics from '../../services/appAnalytics';
import { buildAppAnalyticsContext } from '../../services/appAnalyticsContext';
import { buildAppAnalyticsIdentify } from '../../services/appAnalyticsIdentify';

/**
 * Mask a phone number: show country code + first digit + dots + last 4.
 * e.g. "+254 7•••• 6327"
 */
const maskPhone = (phone: string): string => {
  const digits = phone.replace(/[^0-9]/g, '');
  if (digits.length < 6) return phone;
  const hasPlus = phone.startsWith('+');
  // Find country code boundary (1-3 digits after +)
  let ccLen = 1;
  if (digits.startsWith('254')) ccLen = 3;
  else if (digits.length > 10) ccLen = digits.length - 10 || 1;
  const cc = digits.slice(0, ccLen);
  const rest = digits.slice(ccLen);
  const first = rest.slice(0, 1);
  const last = rest.slice(-4);
  const masked = rest.slice(1, -4).replace(/./g, '•');
  return `${hasPlus ? '+' : ''}${cc} ${first}${masked} ${last}`;
};

/**
 * Mask an email: show first 2 chars + dots + @domain.
 * e.g. "jo••••@gmail.com"
 */
const maskEmail = (email: string): string => {
  const [local, domain] = email.split('@');
  if (!domain || !local) return email;
  const visible = local.slice(0, 2);
  const masked = local.slice(2).replace(/./g, '•');
  return `${visible}${masked}@${domain}`;
};

const isKenyanNumber = (phone: string): boolean =>
  phone.replace(/\s/g, '').startsWith('+254');

const VerificationScreen = ({
  navigation,
  route,
}: {
  navigation: any;
  route?: any;
}) => {
  const phone: string | undefined = route?.params?.phone;
  const email: string | undefined = route?.params?.email;
  const isKenyan = phone ? isKenyanNumber(phone) : true;

  const subtitleText = isKenyan
    ? `We've sent a verification code to your mobile number${phone ? `\n${maskPhone(phone)}` : ''}`
    : `We've sent a verification code to your email${email ? `\n${maskEmail(email)}` : ''}`;
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const { theme } = useTheme();
  const { isLandscape } = useLayoutSize();
  const useWebLikeLayout = shouldUseWebLayout(windowWidth, windowHeight);
  const [code, setCode] = useState('');
  const [counter, setCounter] = useState(60);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [userId, setUserId] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const currentLocationId = useAppSelector(
    state => state.location.currentLocationId,
  );
  const currentLocation = useAppSelector(
    state => state.location.currentLocation,
  );
  // Load stored user ID
  useEffect(() => {
    const loadUserId = async () => {
      const storedId = await AsyncStorage.getItem('userId');
      setUserId(storedId || '');
    };
    loadUserId();
  }, []);

  // Countdown timer
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (counter > 0) {
      timer = setInterval(() => setCounter(prev => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [counter]);

  const handleVerify = async () => {
    const locationType =
      currentLocation?.bp_location_is_system_generated === true
        ? 'test'
        : currentLocation
          ? 'real'
          : undefined;
    const ctx = buildAppAnalyticsContext({ locationType });

    if (!userId) {
      appAnalytics.track('acq_otp_verify_failed', {
        ...ctx,
        error_category: 'authentication',
      });
      setErrMsg('Account not found, redirecting to login...');
      setTimeout(() => navigation.navigate('Login'), 3000);
      return;
    }

    if (!currentLocationId) {
      appAnalytics.track('acq_otp_verify_failed', {
        ...ctx,
        error_category: 'validation',
      });
      setErrMsg('No location found, redirecting to login...');
      // setTimeout(() => navigation.navigate('Login'), 3000);
      return;
    }

    if (code.length < 4) {
      appAnalytics.track('acq_otp_verify_failed', {
        ...ctx,
        error_category: 'validation',
      });
      setErrMsg('Please enter a valid 4-digit code');
      return;
    }

    try {
      setLoading(true);
      const response = await axiosinstance.post(
        'bp_owner_account_verification_information',
        {
          bp_verification_code: code,
          bp_user_id: userId,
          bp_user_business_location_id: currentLocationId,
        },
      );
      console.log('bp_owner_account_verification_information', response.data);
      if (response.data?.accessToken) {
        const accessToken = response.data?.accessToken;
        const refreshToken = response.data?.refreshToken;
        await AsyncStorage.setItem('refreshToken', refreshToken);
        await AsyncStorage.setItem('accessToken', accessToken);

        appAnalytics.track('acq_otp_verify_succeeded', { ...ctx });
        const identify = buildAppAnalyticsIdentify({
          employee: { _id: userId },
          location: currentLocation
            ? {
                ...currentLocation,
                bp_location_type:
                  locationType ?? currentLocation.bp_location_type,
              }
            : currentLocation,
        });
        if (identify) appAnalytics.identify(identify);

        navigation.navigate('BusinessDetailsScreen');
      } else if (response.data?.message.includes('already verified')) {
        appAnalytics.track('acq_otp_verify_failed', {
          ...ctx,
          error_category: 'duplicate',
        });
        setErrMsg('Account already verified, redirecting to login...');
        setTimeout(() => navigation.navigate('Login'), 3000);
      } else {
        appAnalytics.track('acq_otp_verify_failed', {
          ...ctx,
          error_category: 'authentication',
        });
        setErrMsg(response.data?.message || 'Invalid verification code');
      }
    } catch (error: any) {
      appAnalytics.track('acq_otp_verify_failed', {
        ...ctx,
        error_category: error?.response ? 'authentication' : 'network',
      });
      setErrMsg(error?.response?.data?.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    const locationType =
      currentLocation?.bp_location_is_system_generated === true
        ? 'test'
        : currentLocation
          ? 'real'
          : undefined;
    const ctx = buildAppAnalyticsContext({ locationType });
    appAnalytics.track('acq_otp_resend_tapped', { ...ctx });

    if (!userId) {
      setErrMsg('Account not found, redirecting to login...');
      setTimeout(() => navigation.navigate('Login'), 3000);
      return;
    }

    try {
      await axiosinstance.post(
        'bp_owner_resend_verification_code_information',
        {
          bp_user_id: userId,
        },
      );
      setCounter(60);
      setSuccessMsg('New code sent successfully!');
      setErrMsg('');
    } catch (error) {
      console.log('error', error);
      setErrMsg('Failed to resend code. Please try again.');
      setSuccessMsg('');
    }
  };

  // Web layout - centered form card
  if (useWebLikeLayout && isLandscape) {
    return (
      <View style={styles.webContainer}>
        <ScrollView
          style={styles.webScrollView}
          contentContainerStyle={styles.webScrollContent}
          showsVerticalScrollIndicator={false}>
          <View style={styles.webFormCard}>
            <View style={styles.webFormHeader}>
              <Ionicons name="shield-checkmark" size={48} color={theme.colors.success} />
              <Typography style={styles.webTitle}>
                Verify Your Account
              </Typography>
              <Typography style={styles.webSubtitle}>
                {subtitleText}
              </Typography>
            </View>

            {errMsg ? (
              <View style={styles.webErrorContainer}>
                <Typography style={styles.webErrorText}>{errMsg}</Typography>
              </View>
            ) : null}

            {successMsg ? (
              <View style={styles.webSuccessContainer}>
                <Typography style={styles.webSuccessText}>
                  {successMsg}
                </Typography>
              </View>
            ) : null}

            <View style={styles.webInputGroup}>
              <Typography style={styles.webLabel}>Verification Code</Typography>
              <TextInput
                style={styles.webInput}
                placeholder="Enter 4-digit code"
                placeholderTextColor={theme.colors.textPlaceholder}
                keyboardType="number-pad"
                maxLength={4}
                value={code}
                onChangeText={setCode}
                // Web: treat Enter as clicking "Verify Account"
                onKeyPress={(e: any) => {
                  const key = e?.nativeEvent?.key ?? e?.key;
                  if (key !== 'Enter') return;
                  e?.preventDefault?.();
                  e?.stopPropagation?.();
                  if (loading || code.length < 4) return;
                  void handleVerify();
                }}
                // Native: press "done"/"enter" to submit
                returnKeyType="done"
                onSubmitEditing={() => {
                  if (loading || code.length < 4) return;
                  void handleVerify();
                }}
                autoFocus
              />
            </View>

            <TouchableOpacity
              style={[
                styles.webSubmitButton,
                (code.length < 4 || loading) && styles.webDisabledButton,
              ]}
              onPress={handleVerify}
              disabled={code.length < 4 || loading}>
              {loading ? (
                <ActivityIndicator color={theme.colors.textInverse} />
              ) : (
                <Typography style={styles.webButtonText}>
                  Verify Account
                </Typography>
              )}
            </TouchableOpacity>

            <View style={styles.webResendContainer}>
              {counter > 0 ? (
                <Typography style={styles.webTimerText}>
                  Resend code in {counter} seconds
                </Typography>
              ) : (
                <TouchableOpacity onPress={handleResend}>
                  <Typography style={styles.webResendText}>
                    Didn't receive code?{' '}
                    <Typography style={styles.webResendLink}>
                      Resend Now
                    </Typography>
                  </Typography>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </ScrollView>
      </View>
    );
  }

  // Mobile layout
  return (
    <View style={styles.container}>
      <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
        <ScrollView
          contentContainerStyle={styles.content}
          keyboardShouldPersistTaps="always">
          <View style={styles.header}>
            <Ionicons name="shield-checkmark" size={48} color={theme.colors.success} />
            <Typography style={styles.title}>Verify Your Account</Typography>
            <Typography style={styles.subtitle}>
              {subtitleText}
            </Typography>
          </View>

          {errMsg ? (
            <View style={styles.errorContainer}>
              <Typography style={styles.errorText}>{errMsg}</Typography>
            </View>
          ) : null}

          {successMsg ? (
            <View style={styles.successContainer}>
              <Typography style={styles.successText}>{successMsg}</Typography>
            </View>
          ) : null}

          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              placeholder="Enter 4-digit code"
              placeholderTextColor={theme.colors.textPlaceholder}
              keyboardType="number-pad"
              maxLength={4}
              value={code}
              onChangeText={setCode}
              // Web: treat Enter as clicking "Verify Account"
              onKeyPress={(e: any) => {
                const key = e?.nativeEvent?.key ?? e?.key;
                if (key !== 'Enter') return;
                e?.preventDefault?.();
                e?.stopPropagation?.();
                if (loading || code.length < 4) return;
                void handleVerify();
              }}
              // Native: press "done"/"enter" to submit
              returnKeyType="done"
              onSubmitEditing={() => {
                if (loading || code.length < 4) return;
                void handleVerify();
              }}
              autoFocus
            />
          </View>

          <TouchableOpacity
            style={[
              styles.button,
              (code.length < 4 || loading) && styles.disabledButton,
            ]}
            onPress={handleVerify}
            disabled={code.length < 4 || loading}>
            {loading ? (
              <ActivityIndicator color={theme.colors.textInverse} />
            ) : (
              <Typography style={styles.buttonText}>Verify Account</Typography>
            )}
          </TouchableOpacity>

          <View style={styles.resendContainer}>
            {counter > 0 ? (
              <Typography style={styles.timerText}>
                Resend code in {counter} seconds
              </Typography>
            ) : (
              <TouchableOpacity onPress={handleResend}>
                <Typography style={styles.resendText}>
                  Didn't receive code?{'\u00A0'}
                  <Typography style={styles.resendLink}>Resend Now</Typography>
                </Typography>
              </TouchableOpacity>
            )}
          </View>
        </ScrollView>
      </TouchableWithoutFeedback>
    </View>
  );
};

const styles = StyleSheet.create({
  // ============ WEB STYLES ============
  webContainer: {
    flex: 1,
    backgroundColor: theme.colors.backgroundGray50,
    minHeight: '100%',
  },
  webScrollView: {
    flex: 1,
  },
  webScrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: s(32),
    paddingVertical: v(48),
  },
  webFormCard: {
    backgroundColor: theme.colors.surface,
    borderRadius: s(24),
    padding: s(48),
    width: '100%',
    maxWidth: s(480),
    shadowColor: theme.colors.textGray900,
    shadowOffset: { width: 0, height: v(4) },
    shadowOpacity: 0.08,
    shadowRadius: s(24),
    elevation: 8,
  },
  webFormHeader: {
    alignItems: 'center',
    marginBottom: v(32),
  },
  webTitle: {
    fontSize: fs(28),
    lineHeight: fs(34),
    fontWeight: '700',
    color: theme.colors.textGray900,
    fontFamily: GTFonts.semibold,
    marginTop: v(16),
    marginBottom: v(8),
  },
  webSubtitle: {
    fontSize: fs(14),
    color: theme.colors.textGray500,
    fontFamily: GTFonts.regular,
    textAlign: 'center',
    lineHeight: fs(22),
  },
  webInputGroup: {
    marginBottom: v(24),
  },
  webLabel: {
    fontSize: fs(14),
    lineHeight: fs(20),
    fontWeight: '600',
    color: theme.colors.textGray700,
    fontFamily: GTFonts.semibold,
    marginBottom: v(8),
  },
  webInput: {
    backgroundColor: theme.colors.backgroundGray50,
    borderWidth: 1,
    borderColor: theme.colors.primary,
    borderRadius: s(12),
    padding: s(16),
    lineHeight: fs(20),
    fontSize: fs(16),
    color: theme.colors.textGray700,
    fontFamily: GTFonts.semibold,
    textAlign: 'center',
    letterSpacing: 8,
    outlineStyle: 'none',
  } as any,
  webSubmitButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: s(12),
    paddingVertical: v(16),
    alignItems: 'center',
    justifyContent: 'center',
  },
  webDisabledButton: {
    backgroundColor: theme.colors.primaryLight,
  },
  webButtonText: {
    color: theme.colors.textInverse,
    fontSize: fs(16),
    lineHeight: fs(20),
    fontWeight: '600',
    fontFamily: GTFonts.semibold,
  },
  webResendContainer: {
    marginTop: v(24),
    alignItems: 'center',
  },
  webTimerText: {
    color: theme.colors.textGray500,
    fontSize: fs(14),
    lineHeight: fs(20),
    fontFamily: GTFonts.regular,
  },
  webResendText: {
    color: theme.colors.textGray500,
    fontSize: fs(14),
    lineHeight: fs(20),
    fontFamily: GTFonts.regular,
  },
  webResendLink: {
    color: theme.colors.primary,
    fontWeight: '600',
    fontFamily: GTFonts.semibold,
    textDecorationLine: 'underline',
  },
  webErrorContainer: {
    backgroundColor: theme.colors.errorLight,
    borderRadius: s(8),
    padding: s(12),
    marginBottom: v(24),
  },
  webErrorText: {
    color: theme.colors.errorDark,
    textAlign: 'center',
    fontSize: fs(14),
    lineHeight: fs(20),
    fontFamily: GTFonts.regular,
  },
  webSuccessContainer: {
    backgroundColor: theme.colors.successLight,
    borderRadius: s(8),
    padding: s(12),
    marginBottom: v(24),
  },
  webSuccessText: {
    color: theme.colors.successDark,
    textAlign: 'center',
    fontSize: fs(14),
    lineHeight: fs(20),
    fontFamily: GTFonts.regular,
  },
  // ============ MOBILE STYLES ============
  container: {
    flex: 1,
    backgroundColor: theme.colors.whitesmoke,
    justifyContent: 'center',
    padding: s(24),
    marginBottom: v(150),
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    padding: 24,
    marginBottom: 150,
  },
  header: {
    alignItems: 'center',
    marginBottom: 30,
  },
  title: {
    fontSize: fs(24),
    lineHeight: fs(28),
    fontWeight: 'bold',
    color: theme.colors.primary,
    marginVertical: v(16),
  },
  subtitle: {
    fontSize: isTablet() ? 18 : 16,
    color: theme.colors.graphiteDark,
    textAlign: 'center',
    lineHeight: 24,
  },
  errorContainer: {
    backgroundColor: theme.colors.errorLight,
    borderRadius: s(8),
    padding: s(12),
    marginBottom: v(24),
  },
  errorText: {
    color: theme.colors.errorDark,
    textAlign: 'center',
  },
  inputContainer: {
    marginBottom: 24,
  },
  input: {
    backgroundColor: theme.colors.surface,
    borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
    borderColor: theme.colors.primary,
    borderRadius: s(8),
    padding: s(16),
    fontSize: fs(18),
    lineHeight: fs(22),
    textAlign: 'center',
    letterSpacing: 4,
    marginBottom: v(16),
  },
  button: {
      backgroundColor: theme.colors.primary,
    borderRadius: s(8),
    padding: s(16),
    alignItems: 'center',
  },
  disabledButton: {
    backgroundColor: theme.colors.primaryLight,
  },
  buttonText: {
    color: theme.colors.textInverse,
    fontSize: isTablet() ? 18 : 16,
    lineHeight: fs(isTablet() ? 22 : 20),
    fontWeight: '600',
  },
  resendContainer: {
    marginTop: 24,
    alignItems: 'center',
  },
  timerText: {
    color: theme.colors.graphiteDark,
  },
  resendText: {
    color: theme.colors.graphiteDark,
  },
  resendLink: {
    color: theme.colors.primaryDark,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  successContainer: {
    backgroundColor: theme.colors.successLight,
    borderRadius: s(8),
    padding: s(12),
    marginBottom: v(24),
  },
  successText: {
    color: theme.colors.successDarker,
    textAlign: 'center',
  },
});

export default VerificationScreen;
